import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useData } from '../DataContext';
import { X, Car, Calendar, Tag } from 'lucide-react';

export default function ExpensesPage() {
  const { expenses, setExpenses, cars } = useData();
  const [showModal, setShowModal] = useState(false);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [search, setSearch] = useState('');
  const [amountInput, setAmountInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL + '/api',
    headers: { Authorization: `Bearer ${token}` },
  });

  function formatMoney(value) {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }

  function parseMoney(value) {
    if (!value) return 0;
    return parseFloat(value.replace(/\s/g, '').replace(',', '.')) || 0;
  }

  function handleAmountChange(e) {
    const value = e.target.value;
    const cleaned = value.replace(/[^\d.,]/g, '');
    setAmountInput(cleaned);
  }

  function handleAmountBlur() {
    if (amountInput) {
      const cleaned = amountInput.replace(/\s/g, '').replace(',', '.');
      const num = parseFloat(cleaned);
      if (!isNaN(num)) {
        setAmountInput(formatMoney(num));
      }
    }
  }

  function handleAmountFocus() {
    if (amountInput) {
      const cleaned = amountInput.replace(/\s/g, '').replace(',', '.');
      setAmountInput(cleaned);
    }
  }

  // 👁️ Просмотр расхода
  function handleViewExpense(expense) {
    setViewingExpense(expense);
    setIsEditing(false);
    setAmountInput(formatMoney(expense.amount));
    setShowModal(true);
  }

  // ✅ Добавление / редактирование
  async function handleSaveExpense(e) {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const form = e.target;
      const data = {
        carId: form.carId.value,
        type: form.type.value,
        amount: parseMoney(amountInput),
        date: form.date.value ? new Date(form.date.value).toISOString() : new Date().toISOString(),
      };

      if (viewingExpense && isEditing) {
        // Обновление
        const res = await api.put(`/expenses/${viewingExpense._id}`, data);
        const updatedExpense = {
          ...res.data,
          carId: cars.find((c) => c._id === data.carId) || res.data.carId,
        };
        setExpenses((prev) => prev.map((e) => (e._id === viewingExpense._id ? updatedExpense : e)));
      } else {
        // Создание
        const res = await api.post('/expenses', data);
        const newExpense = {
          ...res.data,
          carId: cars.find((c) => c._id === data.carId) || res.data.carId,
        };
        setExpenses((prev) => [newExpense, ...prev]);
      }

      setShowModal(false);
      setViewingExpense(null);
      setIsEditing(false);
      setAmountInput('');
    } catch (err) {
      console.error('Ошибка при сохранении расхода:', err);
      alert('Не удалось сохранить расход');
    } finally {
      setSubmitting(false);
    }
  }

  // 🗑️ Удаление
  async function handleDeleteExpense() {
    if (!viewingExpense) return;
    if (!window.confirm(`Удалить расход "${viewingExpense.type}"?`)) return;
    
    setSubmitting(true);
    try {
      await api.delete(`/expenses/${viewingExpense._id}`);
      setExpenses((prev) => prev.filter((e) => e._id !== viewingExpense._id));
      setShowModal(false);
      setViewingExpense(null);
      setIsEditing(false);
    } catch (err) {
      console.error('Ошибка при удалении расхода:', err);
      alert('Не удалось удалить расход');
    } finally {
      setSubmitting(false);
    }
  }

  function getCurrentDateTime() {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  }

  // 🔍 Фильтрация и сортировка
  const filteredExpenses = useMemo(() => {
    const q = search.toLowerCase();
    let result = expenses.filter(
      (e) =>
        e.type.toLowerCase().includes(q) ||
        e.carId?.name?.toLowerCase().includes(q) ||
        String(e.amount).includes(q)
    );

    result = [...result].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });

    return result;
  }, [expenses, search]);

  // 📊 Статистика
  const expenseTypes = useMemo(() => {
    return [...new Set(expenses.map((e) => e.type))].sort();
  }, [expenses]);

  const expensesByType = useMemo(() => {
    const grouped = {};
    expenses.forEach((expense) => {
      if (!grouped[expense.type]) {
        grouped[expense.type] = {
          type: expense.type,
          total: 0,
          count: 0,
        };
      }
      grouped[expense.type].total += expense.amount;
      grouped[expense.type].count += 1;
    });
    return Object.values(grouped).sort((a, b) => b.total - a.total);
  }, [expenses]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold">Расходы</h1>
          <p className="text-zinc-400 text-sm mt-1">Всего расходов: {expenses.length}</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Поиск..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 sm:flex-none bg-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            onClick={() => {
              setViewingExpense(null);
              setIsEditing(false);
              setAmountInput('');
              setShowModal(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded transition-colors w-full sm:w-auto"
          >
            Добавить
          </button>
        </div>
      </div>

      {/* 📊 Статистика */}
      {expensesByType.length > 0 && (
        <div className="mb-4 bg-zinc-900 rounded-xl border border-zinc-800 p-4">
          <h3 className="text-sm font-medium text-zinc-400 mb-3">Статистика по типам</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {expensesByType.map((item) => (
              <div
                key={item.type}
                className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50 hover:border-purple-500/30 transition-all"
              >
                <div className="text-xs text-zinc-500 mb-1">{item.type}</div>
                <div className="text-lg font-bold text-purple-400">{formatMoney(item.total)}</div>
                <div className="text-xs text-zinc-600 mt-1">{item.count} раз</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 💻 Таблица для десктопа */}
      <div className="hidden md:block bg-zinc-900 rounded-xl overflow-hidden border border-zinc-800">
        <table className="w-full text-left">
          <thead className="bg-zinc-800/50">
            <tr className="text-zinc-400 text-sm">
              <th className="p-4 font-medium">Машина</th>
              <th className="p-4 font-medium">Тип расхода</th>
              <th className="p-4 font-medium">Сумма</th>
              <th className="p-4 font-medium">Дата и время</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {filteredExpenses.map((e) => (
              <tr 
                key={e._id} 
                className="hover:bg-zinc-800/30 transition-colors cursor-pointer"
                onClick={() => handleViewExpense(e)}
              >
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                      <Car className="w-5 h-5 text-blue-400" />
                    </div>
                    <span className="text-white font-medium">{e.carId?.name || '—'}</span>
                  </div>
                </td>
                <td className="p-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-500/10 text-purple-400 border border-purple-500/20">
                    {e.type}
                  </span>
                </td>
                <td className="p-4">
                  <span className="text-emerald-400 font-semibold text-lg">
                    {formatMoney(e.amount)}
                  </span>
                </td>
                <td className="p-4 text-zinc-400">
                  {new Date(e.date).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 📱 Карточки для мобильных */}
      <div className="grid md:hidden gap-3">
        {filteredExpenses.map((e) => (
          <div
            key={e._id}
            onClick={() => handleViewExpense(e)}
            className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 shadow-sm hover:border-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Car className="w-4 h-4 text-blue-400" />
                </div>
                <div className="font-semibold text-white">{e.carId?.name || '—'}</div>
              </div>
              <div className="text-xs text-zinc-500">
                {new Date(e.date).toLocaleString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-500/10 text-purple-400 border border-purple-500/20">
                {e.type}
              </span>
              <div className="text-emerald-400 font-semibold text-lg">{formatMoney(e.amount)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Пустое состояние */}
      {filteredExpenses.length === 0 && (
        <div className="text-center py-12 bg-zinc-900 rounded-xl border border-zinc-800">
          <h3 className="text-xl font-semibold text-zinc-100 mb-2">
            {search ? 'Расходы не найдены' : 'Нет расходов'}
          </h3>
          <p className="text-zinc-400 mb-6">
            {search ? 'Попробуйте изменить параметры поиска' : 'Добавьте первый расход'}
          </p>
        </div>
      )}

      {/* 🔹 Модалка */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl w-full max-w-md border border-zinc-700/50 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent">
                  {viewingExpense && !isEditing 
                    ? 'Информация о расходе' 
                    : viewingExpense 
                    ? 'Редактировать расход' 
                    : 'Добавить расход'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setViewingExpense(null);
                    setIsEditing(false);
                    setAmountInput('');
                  }}
                  className="text-zinc-400 hover:text-zinc-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {viewingExpense && !isEditing ? (
                /* 👁️ Режим просмотра */
                <div className="space-y-4">
                  <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Car className="w-5 h-5 text-blue-400" />
                      <div>
                        <div className="text-xs text-zinc-500">Машина</div>
                        <div className="text-white font-semibold">{viewingExpense.carId?.name || '—'}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-3 border-t border-zinc-700">
                      <Tag className="w-5 h-5 text-purple-400" />
                      <div>
                        <div className="text-xs text-zinc-500">Тип расхода</div>
                        <div className="text-white font-semibold">{viewingExpense.type}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-3 border-t border-zinc-700">
                      
                      <div>
                        <div className="text-xs text-zinc-500">Сумма</div>
                        <div className="text-emerald-400 font-bold text-xl">
                          {formatMoney(viewingExpense.amount)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-3 border-t border-zinc-700">
                      <Calendar className="w-5 h-5 text-zinc-400" />
                      <div>
                        <div className="text-xs text-zinc-500">Дата и время</div>
                        <div className="text-white">
                          {new Date(viewingExpense.date).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleDeleteExpense}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                      disabled={submitting}
                    >
                      {submitting ? 'Удаление...' : 'Удалить'}
                    </button>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                      disabled={submitting}
                    >
                      Изменить
                    </button>
                  </div>
                </div>
              ) : (
                /* ✏️ Режим редактирования / создания */
                <form onSubmit={handleSaveExpense} className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm text-zinc-400">Машина *</label>
                    <select
                      name="carId"
                      defaultValue={viewingExpense?.carId?._id || ''}
                      required
                      className="w-full p-3 rounded-lg bg-zinc-800 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                    >
                      <option value="">Выбрать машину</option>
                      {cars.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm text-zinc-400">Тип расхода *</label>
                    <input
                      name="type"
                      type="text"
                      list="expense-types"
                      defaultValue={viewingExpense?.type || ''}
                      placeholder="ТО, ремонт, топливо, мойка..."
                      className="w-full p-3 rounded-lg bg-zinc-800 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                      required
                    />
                    <datalist id="expense-types">
                      {expenseTypes.map((type) => (
                        <option key={type} value={type} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm text-zinc-400">Сумма *</label>
                    <input
                      name="amount"
                      type="text"
                      placeholder="Введите сумму"
                      value={amountInput}
                      onChange={handleAmountChange}
                      onFocus={handleAmountFocus}
                      onBlur={handleAmountBlur}
                      className="w-full p-3 rounded-lg bg-zinc-800 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                      required
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm text-zinc-400">Дата и время *</label>
                    <input
                      name="date"
                      type="datetime-local"
                      defaultValue={
                        viewingExpense 
                          ? new Date(viewingExpense.date).toISOString().slice(0, 16)
                          : getCurrentDateTime()
                      }
                      className="w-full p-3 rounded-lg bg-zinc-800 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                      required
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (viewingExpense) {
                          setIsEditing(false);
                        } else {
                          setShowModal(false);
                        }
                        setAmountInput('');
                      }}
                      className="flex-1 px-5 py-2.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-colors"
                      disabled={submitting}
                    >
                      Назад
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-5 py-2.5 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 transition-all disabled:opacity-50"
                      disabled={submitting}
                    >
                      {submitting ? 'Сохранение...' : (viewingExpense ? 'Сохранить' : 'Создать')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}